import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Briefcase,
  Flame,
  Search,
  Users,
  TrendingUp,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SECTOR_LABELS = {
  hotellerie: 'Hôtellerie',
  restauration: 'Restauration',
  artisan: 'Artisan / BTP',
  commerce: 'Commerce',
  liberal: 'Libéral',
  services: 'Services',
  industrie: 'Industrie',
  autre: 'Autre',
};

const STATUS_LABELS = {
  new: 'Nouveau',
  contacted: 'Contacté',
  replied: 'A répondu',
  interested: 'Intéressé',
  trial: 'Essai',
  customer: 'Client',
  lost: 'Perdu',
};

const STATUS_COLORS = {
  new: 'bg-slate-100 text-slate-700 border-slate-300',
  contacted: 'bg-blue-100 text-blue-700 border-blue-300',
  replied: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  interested: 'bg-purple-100 text-purple-700 border-purple-300',
  trial: 'bg-amber-100 text-amber-700 border-amber-300',
  customer: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  lost: 'bg-red-100 text-red-700 border-red-300',
};

const scoreColor = (score) => {
  if (score >= 8) return 'bg-red-100 text-red-700 border-red-300';
  if (score >= 5) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-slate-100 text-slate-600 border-slate-300';
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Prospects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSector, setFilterSector] = useState('all');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Garde super_admin
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      toast.error('Accès réservé au super administrateur');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/prospects/stats`, {
        headers: getAuthHeaders(),
      });
      setStats(response.data);
    } catch (err) {
      console.error('fetchStats error', err);
    }
  }, []);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSector !== 'all') params.sector = filterSector;
      if (search && search.trim().length > 1) params.search = search.trim();
      const response = await axios.get(`${API_URL}/dashboard/prospects`, {
        headers: getAuthHeaders(),
        params,
      });
      setProspects(response.data || []);
    } catch (err) {
      console.error('fetchProspects error', err);
      toast.error('Impossible de charger les prospects');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSector, search]);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchStats();
    }
  }, [user, fetchStats]);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      const timeout = setTimeout(() => fetchProspects(), 300);
      return () => clearTimeout(timeout);
    }
  }, [user, fetchProspects]);

  const openDetail = async (siret) => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/prospects/${siret}`, {
        headers: getAuthHeaders(),
      });
      setSelectedProspect(response.data);
      setDialogOpen(true);
    } catch (err) {
      toast.error('Erreur chargement détail');
    }
  };

  if (!user || user.role !== 'super_admin') {
    return null;
  }

  const totalByStatus = (status) =>
    stats?.by_status?.[status] || 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Briefcase className="h-8 w-8 text-[#0F5156]" />
            Prospects commerciaux
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            CRM platform-wide — sociétés contactées par Lucas (agent commercial autonome)
          </p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{stats?.total ?? '—'}</div>
            <Users className="h-5 w-5 text-slate-400" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Nouveaux</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{totalByStatus('new')}</div>
            <Building2 className="h-5 w-5 text-slate-400" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Contactés</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">{totalByStatus('contacted')}</div>
            <Mail className="h-5 w-5 text-blue-400" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Hot (≥ 8)</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold text-red-600">
              {stats?.hot_leads_count ?? 0}
            </div>
            <Flame className="h-5 w-5 text-red-500" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Clients</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold text-emerald-600">
              {stats?.customers ?? 0}
            </div>
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Conversion</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div className="text-2xl font-bold">
              {stats?.conversion_rate ?? 0}%
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-500" />
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, SIRET ou email…"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={filterSector} onValueChange={setFilterSector}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Secteur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous secteurs</SelectItem>
                {Object.entries(SECTOR_LABELS).map(([code, label]) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([code, label]) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setFilterStatus('all');
                setFilterSector('all');
              }}
            >
              Réinitialiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#0F5156]" />
            </div>
          ) : prospects.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-lg">Aucun prospect</p>
              <p className="text-sm mt-1">
                Lucas ajoutera des prospects ici à chaque prospection hebdomadaire.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Société</TableHead>
                  <TableHead>Secteur</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead className="text-right">Effectif</TableHead>
                  <TableHead className="text-center">Score</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Dernier contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prospects.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                    onClick={() => openDetail(p.siret)}
                  >
                    <TableCell>
                      <div className="font-medium">{p.company_name}</div>
                      <div className="text-xs text-slate-500">{p.siret}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {SECTOR_LABELS[p.sector] || p.sector || 'Autre'}
                      </Badge>
                    </TableCell>
                    <TableCell>{p.ville || '—'}</TableCell>
                    <TableCell className="text-right">
                      {p.employee_count ?? '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={scoreColor(p.score)}>{p.score}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[p.status] || ''}>
                        {STATUS_LABELS[p.status] || p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(p.last_contact_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog détail */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedProspect && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-[#0F5156]" />
                  {selectedProspect.company_name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-slate-500">SIRET</div>
                    <div className="font-mono">{selectedProspect.siret}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">NAF</div>
                    <div>
                      {selectedProspect.naf_code}
                      {selectedProspect.naf_label && (
                        <span className="text-slate-500 ml-2">
                          ({selectedProspect.naf_label})
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500">Secteur</div>
                    <Badge variant="outline">
                      {SECTOR_LABELS[selectedProspect.sector] ||
                        selectedProspect.sector ||
                        'Autre'}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-slate-500">Effectif</div>
                    <div>{selectedProspect.employee_count ?? '—'} salariés</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-slate-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Adresse
                    </div>
                    <div>
                      {[
                        selectedProspect.address,
                        selectedProspect.code_postal,
                        selectedProspect.ville,
                      ]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </div>
                  </div>
                  {selectedProspect.email && (
                    <div>
                      <div className="text-slate-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email
                      </div>
                      <div className="truncate">{selectedProspect.email}</div>
                    </div>
                  )}
                  {selectedProspect.phone && (
                    <div>
                      <div className="text-slate-500 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> Téléphone
                      </div>
                      <div>{selectedProspect.phone}</div>
                    </div>
                  )}
                  {selectedProspect.website && (
                    <div className="col-span-2">
                      <div className="text-slate-500 flex items-center gap-1">
                        <Globe className="h-3 w-3" /> Site web
                      </div>
                      <a
                        href={selectedProspect.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#0F5156] hover:underline break-all"
                      >
                        {selectedProspect.website}
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2 border-t">
                  <Badge className={scoreColor(selectedProspect.score)}>
                    Score {selectedProspect.score}
                  </Badge>
                  <Badge className={STATUS_COLORS[selectedProspect.status] || ''}>
                    {STATUS_LABELS[selectedProspect.status] || selectedProspect.status}
                  </Badge>
                  <div className="text-xs text-slate-500 ml-auto">
                    Source : {selectedProspect.source}
                  </div>
                </div>

                {/* Contact history timeline */}
                <div className="pt-3 border-t">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Historique des contacts (
                    {selectedProspect.contact_history?.length || 0})
                  </h3>
                  {selectedProspect.contact_history?.length > 0 ? (
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {selectedProspect.contact_history
                        .slice()
                        .reverse()
                        .map((entry, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm"
                          >
                            <div className="mt-0.5">
                              {entry.result === 'replied' ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              ) : entry.result === 'bounced' ||
                                entry.result === 'no_answer' ? (
                                <XCircle className="h-4 w-4 text-red-500" />
                              ) : (
                                <Mail className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium capitalize">
                                  {entry.type}
                                </span>
                                {entry.template && (
                                  <Badge variant="outline" className="text-xs">
                                    {entry.template}
                                  </Badge>
                                )}
                                <span className="text-xs text-slate-500 ml-auto">
                                  {formatDateTime(entry.timestamp)}
                                </span>
                              </div>
                              {entry.subject && (
                                <div className="text-slate-700 dark:text-slate-300 mt-1">
                                  {entry.subject}
                                </div>
                              )}
                              {entry.note && (
                                <div className="text-xs text-slate-500 mt-1">
                                  {entry.note}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      Aucun contact enregistré.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Prospects;
